import React from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormModal } from '@/components/ui/form-modal';

type RequestApprovalSuccessModalProps = {
  show: boolean;
  onHide: () => void;
  draftName: string;
  editedBy: string;
  incidentId: string | null;
};

const RequestApprovalSuccessModal: React.FC<
  RequestApprovalSuccessModalProps
> = ({ show, onHide, draftName, editedBy, incidentId }) => {
  const isAorOnly = !incidentId;

  return (
    <FormModal
      open={show}
      onOpenChange={(open) => {
        if (!open) onHide();
      }}
      title="Approval Requested"
      description={
        <>
          Draft <b>{draftName}</b> by <b>{editedBy}</b> has been submitted.
          Here&apos;s what happens next:
        </>
      }
      widthClassName="tw:!max-w-2xl tw:sm:!max-w-2xl"
      footer={
        <Button type="button" onClick={onHide}>
          OK
        </Button>
      }
    >
      {isAorOnly ? (
        <div className="tw:space-y-3">
          <Card size="sm">
            <CardHeader>
              <CardTitle>Review and Approval</CardTitle>
            </CardHeader>
            <CardContent className="tw:space-y-2 tw:text-sm tw:text-muted-foreground">
              <p className="tw:m-0">
                Every user with permission to approve SITREPs for {draftName}{' '}
                has been notified.
              </p>
              <p className="tw:m-0">
                Any of those reviewers can open this draft and either{' '}
                <b>approve</b> or <b>reject</b> it - no fixed sequence or
                hand-off is required
              </p>
            </CardContent>
          </Card>
          <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
            You&apos;ll be notified as each reviewer takes action. Track
            progress under the <b>Drafts</b> tab.
          </p>
        </div>
      ) : (
        <div className="tw:space-y-3 tw:text-sm tw:text-muted-foreground">
          <div className="tw:font-medium tw:text-foreground">
            Approval Workflow for Incident SITREPs
          </div>
          <ol className="tw:m-0 tw:space-y-2 tw:pl-4">
            <li>
              All users assigned to the <b>Situation Unit Leader</b> position
              are notified to complete the first review.
            </li>
            <li>
              Once the first review is complete, the draft routes to all users
              assigned to the <b>Planning Section Chief</b> position for review
              and approval.
            </li>
            <li>
              Every user assigned to the <b>Incident Commander</b> position is
              notified at submit and sees the report in <b>Drafts</b>{' '}
              immediately.
            </li>
          </ol>
          <p className="tw:m-0">
            Incident Commanders can approve or reject at any point, regardless
            of where the report is in the workflow above or the actions of the
            other reviewers.
          </p>
          <p className="tw:m-0">
            You&apos;ll be notified as each reviewer takes action. Track
            progress under the <b>Drafts</b> tab.
          </p>
        </div>
      )}
    </FormModal>
  );
};

export default RequestApprovalSuccessModal;
